import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {keyCodes} from '@pb/kit-util-key-codes';
import {useComponentDidUpdate} from '@pb/kit-hooks-use-component-did-update';

import {Input} from '../input';
import {valueIsValid} from './utils/validate-value';
import {formattedValue as formattedValueUtil} from './utils/formatted-value';
import {ERROR_MESSAGES, ValidType} from './utils/constants';

import type {Options} from '@pb/kit-formatters';

import type {InputProps} from '../input';
import type {UserLocaleDto, ValidTypes} from './utils/constants';

type ERROR_TYPES = 'WRONG_YEAR' | 'WRONG_MONTH' | 'WRONG_DATE'
    | 'MAX_NUMBER_ERROR' | 'WRONG_NUMBER_VALUE' | 'WRONG_HTTP_PORT' | 'INVALID_DATE_RANGE';

const FORMAT_TYPES = [
    ValidType.INT,
    ValidType.INT_32,
    ValidType.INT_POSITIVE,
    ValidType.NUMBER,
    ValidType.NUMBER_POSITIVE,
    ValidType.NUMBER_0_TO_100,
    ValidType.MAX_SAFE_NUMBER,
    ValidType.MAX_SAFE_NUMBER_POSITIVE,
    ValidType.DATE,
    ValidType.DATE_RANGE,
    ValidType.YEAR,
    ValidType.PHONE,
    ValidType.HTTP_PORT,
] as const;

function isInFormatTypes(type: string) {
    return FORMAT_TYPES.includes(type as never);
}

type OmittedInputProps = Omit<InputProps, 'onBlur'>;

export interface TypedInputProps extends OmittedInputProps {
    /**
     * How input value should be validated
     */
    validateType: ValidTypes,
    /**
     * User settings
     */
    locale?: UserLocaleDto,
    /**
     * Callback on validation update to customize error message
     */
    onValidationUpdate?: (errorType?: ERROR_TYPES, errorMessage?: string) => void,
    /**
     * OnBlur event, it returns formatted value unlike onChange
     * */
    onBlur?: (formattedValue: string, event: React.FocusEvent<HTMLInputElement>) => void,

    /**
     * Options for formatting utility
     * */
    formattingOptions?: Options,
}

/**
* @deprecated It will be removed in 6 months 06-11-2026. Use NumberFormattedInput or PatternFormattedInput from "@pb/kit-component-formatted-input" instead.
* @see [NumberFormattedInput]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-formatted-input-number--documentation}
* @see [PatternFormattedInput]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-formatted-input-pattern--documentation}
*
* @description
* Component for input with validation, formatting and locale.
*
* ## Features
* Can show error messages using onValidationUpdate callback with FieldDescription component.
*
* Returns formatted value on blur.
*
* Returns raw event on change.
*
* Returns error type and error message on onValidationUpdate.
*
* ## Error Handling
* Use onValidationUpdate callback to get error messages and display them with FieldDescription component:
* @example
* ```tsx
* const [error, setError] = useState<string>();
* <TypedInput
*   color={error ? 'ERROR' : undefined}
*   onValidationUpdate={(errorType, errorMessage) => setError(errorMessage)}
*   validateType="int"
*   aria-describedby="error-description"
* />
* <FieldDescription color={error ? 'Types.ERROR' : undefined} id="error-description">
*   {error || 'Helper text'}
* </FieldDescription>
* ```
*
* ## Migration to V2
* TypedInput contains validation, formatting, error message from old \`@pb/kit-component-input\` Input component.
*
* ### Accessibility
It accepts all native input attributes that are available in HTML. Please follow standard requirement for accessibility, e.g. https://www.w3.org/WAI/tutorials/forms/instructions/.
- Use `aria-label` or `aria-labelledby` to provide a label for the input.
- Use `aria-describedby` to provide a description for the input.
- Use `aria-invalid` when there is an error.
* */
export const TypedInput = forwardRef<HTMLInputElement, TypedInputProps>(function TypedInput(props, ref) {
    const {
        validateType,
        onValidationUpdate,
        locale = {
            thousandFormat: 'SPACE',
            decimalFormat: 'COMMA',
            dateFormat: 'MMDDYYYY',
        },
        inputComponent: InputComponent = Input,
        formattingOptions,
        ...inputProps
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current);
    const [value, setValue] = useState<string | number | readonly string[] | undefined>(inputProps.value ?? '');
    const [errorType, setErrorType] = useState<ERROR_TYPES | undefined>(undefined);

    useEffect(() => {
        setValue(inputProps.value);
    }, [inputProps.value]);

    useComponentDidUpdate(() => {
        onValidationUpdate?.(errorType, errorType ? ERROR_MESSAGES[errorType] : undefined);
    }, [errorType]);

    const getFormattedValue = (valueToFormat: string) => formattedValueUtil(
        valueToFormat,
        validateType,
        {
            ...locale,
            formattedValue: validateType === ValidType.YEAR ? 'DATE_YEAR' : locale.formattedValue,
        },
        formattingOptions,
    );

    const getValidatedValue = (valueToValidate: string) => {
        const originValue = valueToValidate.trim();
        const formattedValue = getFormattedValue(originValue);
        return {
            value: isInFormatTypes(validateType) ? formattedValue.value : originValue,
            errorType: formattedValue.errorType,
        };
    };
    const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const data = event.clipboardData
            ? event.clipboardData
            // @ts-expect-error
            : window.clipboardData;
        let pastedValue;
        if (validateType === ValidType.DATE_RANGE) {
            pastedValue = data.getData('Text').trim();
        } else {
            pastedValue = data.getData('Text').replace(/\s+/g, '');
        }

        const formattedValue = getFormattedValue(pastedValue);
        const cursorPositionStart = inputRef.current?.selectionStart ?? 0;
        const cursorPositionEnd = inputRef.current?.selectionEnd ?? 0;

        if (formattedValue.errorType) {
            event.preventDefault();
            return;
        }

        if (validateType === ValidType.DATE && formattedValue.value) {
            return;
        }

        const isValidValue = valueIsValid({
            key: '',
            value: pastedValue,
            validateType,
            cursorPositionStart,
            cursorPositionEnd,
            locale,
        });

        if (!isValidValue) {
            event.preventDefault();
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const {key, ctrlKey, metaKey} = event;
        const cursorPositionStart = inputRef.current?.selectionStart ?? 0;
        const cursorPositionEnd = inputRef.current?.selectionEnd ?? 0;
        const allowedKeys = [keyCodes.KEY_TAB, keyCodes.KEY_INSERT];
        const isSpaceAllowed = key === keyCodes.KEY_SPACE && validateType === ValidType.DATE_RANGE;

        if (ctrlKey || metaKey || allowedKeys.includes(key) || isSpaceAllowed || valueIsValid({
            key,
            value: event.currentTarget.value,
            validateType,
            cursorPositionStart,
            cursorPositionEnd,
            locale,
        })) {
            props.onKeyDown?.(event);
        } else {
            event.preventDefault();
        }
    };


    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.currentTarget.value);

        if (!getValidatedValue(String(value)).errorType) {
            props.onChange?.(event);
        }
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const validatedValue = getValidatedValue(event.currentTarget.value);
        setValue(validatedValue.value);
        setErrorType(validatedValue.errorType);
        props.onBlur?.(validatedValue.value, event);
    };

    const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setErrorType(undefined);
        props.onFocus?.(event);
    };

    useEffect(() => {
        const validatedValue = getValidatedValue(String(inputProps.value ?? ''));
        setValue(validatedValue.value);
        setErrorType(validatedValue.errorType);
    }, [inputProps.value]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <InputComponent
            {...inputProps}
            value={value}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={onChange}
            onInput={event => {
                setValue(event.currentTarget.value);
                props.onInput?.(event);
            }}
            ref={inputRef}
        />
    );
});

TypedInput.displayName = 'TypedInput';
